import { createSelector } from '@reduxjs/toolkit';
import { Activity } from 'gql/autogen/Activity';
import { ActivityWithContent } from 'gql/autogen/ActivityWithContent';
import { PageContentStatus } from 'core/type/PageContentStatus';
import {
    selectActivityFromDetail,
    selectAvailabilityIsSearchCriteriaUpdated,
    selectAvailabilitySelectedDate,
    selectAvailabilityState,
    selectPageContentStatus,
} from 'component/page/detail/store/availabilitySelectorIndex';
import { setSelectedDateStateFromQueryParams } from 'component/page/detail/store/availabilityIndex';
import { selectCmsFromBootstrap, selectFeatureBehaviorFromBootstrap, selectPrerenderStateFromBootstrap } from 'store/slice/bootstrap';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { Offer } from 'gql/autogen/Offer';
import { getValidOffers } from 'component/page/detail/PackageOptions/PackageOptionsUtils';
import { selectActivityTokenFromDetailState } from 'component/page/detail/store/detail.selectors';

export interface OwnProps {
    isScrolling: boolean;
    href: string;
}

export interface StateProps {
    activityId: number;
    activityTokenFromDetail: string;
    errorMessage: string;
    isError: boolean;
    isLoading: boolean;
    isSuccess: boolean;
    isLoaded: boolean;
    isPaxUpdate: boolean;
    contentStatus: PageContentStatus;
    selectedDate: Date;
    cms: CmsValue;
    validAvailableOffers: Offer[];
    validUnavailableOffers: Offer[];
    isContinueYourBookingFeature: boolean;
    activity?: Activity;
    activityFromDetail?: ActivityWithContent;
    earliestAvailabilityDate?: Date;
    isRecommendedActivitiesOnDetailPage?: boolean;
}

export interface DispatchProps {
    setSelectedDateFromQueryParams: (locationSearch: string) => void;
}

export type Props = OwnProps & StateProps & DispatchProps;

export const mapStateToProps = createSelector(
    selectActivityFromDetail,
    selectAvailabilityState,
    selectPageContentStatus,
    selectAvailabilityIsSearchCriteriaUpdated,
    selectAvailabilitySelectedDate,
    selectCmsFromBootstrap,
    selectActivityTokenFromDetailState,
    selectPrerenderStateFromBootstrap,
    selectFeatureBehaviorFromBootstrap,
    (activityFromDetail, state, contentStatus, isPaxUpdate, selectedDate, cms, activityTokenFromDetail, isPrerender, featureBehaviours): StateProps => {
        const activity = state.isSuccess ? state.availabilityResponse?.result?.activity || undefined : undefined;
        const earliestAvailabilityDate = state.earliestAvailableDate;
        const offers = activity?.offerPricing?.offers || [];
        const { validAvailableOffers, validUnavailableOffers } = getValidOffers(offers);
        const isRecommendedActivitiesOnDetailPage = featureBehaviours.RecommendedActivitiesOnDetailPage;
        const isContinueYourBookingFeature = featureBehaviours.ContinueYourBooking;
        return {
            activity,
            activityFromDetail,
            activityTokenFromDetail,
            contentStatus,
            isPaxUpdate,
            selectedDate,
            earliestAvailabilityDate,
            cms,
            validAvailableOffers,
            validUnavailableOffers,
            isRecommendedActivitiesOnDetailPage,
            isContinueYourBookingFeature,
            activityId: activityFromDetail.masterActivityId || 0,
            errorMessage: (state.error?.message || '').toLowerCase(),
            isError: state.isError,
            isLoading: state.isLoading && !isPrerender,
            isSuccess: state.isSuccess,
            isLoaded: state.isSuccess || state.isError,
        };
    },
);

export const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
    setSelectedDateFromQueryParams: (locationSearch: string) => {
        dispatch(setSelectedDateStateFromQueryParams({ locationSearch }));
    },
});
